import { motion } from 'motion/react';
import { useState } from 'react';
import { Play, ExternalLink } from 'lucide-react';
import { VideoModal } from './VideoModal';
import logoTranslation from "../../assets/logos/Anuvadini_Translation.png";
import logoPrint from "../../assets/logos/Anuvadini_Print.png";
import logoGeoAi from "../../assets/logos/Anuvadini-GEO-AI.png";
import logoAgents from "../../assets/logos/Anuvadini_AI_Agents.png";
import logoConnect from "../../assets/logos/Anuvadini_Connect.png";
import logoDevelop from "../../assets/logos/Anuvadini_Develop.png";
import { videoMappings, generateDriveUrl } from '../data/videoMappings';
import { productNavigationLinks } from '../data/productContent';

const serviceCards = [
  {
    title: "Anuvadini Translation",
    subtitle: "MULTILINGUAL LLM + HUMAN EXPERTISE",
    desc: "Create and translate content across 22 Indian and 89+ global languages using our in-house LLM and expert linguists. 4L+ publications created and 2B+ pages translated across 89+ domains with a two-level human validation process.",
    logo: logoTranslation,
    gradient: "from-[#4f46e5] via-[#6366f1] to-[#4f46e5]",
    buttonBg: "bg-[#c2410c]",
    shadow: "shadow-orange-200",
    videoId: "anuvadini-ai-qa-generator",
    thumbnail: "https://hyped.aicte-india.org/img/hyped.png"
  },
  {
    title: "Anuvadini Print",
    subtitle: "DRAG-AND-DROP PUBLISHING STUDIO",
    desc: "Design, format, and publish professional books, research papers, and novels without complex software. Collaborate in real time, apply advanced typography, and export InDesign, PDF, Word, PNG, and JPG formats instantly.",
    logo: logoPrint,
    gradient: "from-[#0ea5e9] via-[#38bdf8] to-[#22d3ee]",
    buttonBg: "bg-[#3730a3]",
    shadow: "shadow-indigo-200",
    videoId: "anuvadini-print-demo-1",
    thumbnail: "https://drive.google.com/thumbnail?id=1A_4J_htxQ6FU6AwxFmyK0x0mY1YjZB_c&sz=w1000-h600"
  },
  {
    title: "Anuvadini GEO-AI",
    subtitle: "MAKE-IN-INDIA GEOSPATIAL INTELLIGENCE",
    desc: "Unlock precise insights across coastlines, rivers, forests, and agriculture. Map urban assets, land use, and infrastructure, and monitor construction in real time for faster, data-driven decisions.",
    logo: logoGeoAi,
    gradient: "from-[#a855f7] via-[#c084fc] to-[#8b5cf6]",
    buttonBg: "bg-[#9d174d]",
    shadow: "shadow-rose-200",
    videoId: "anuvadini-ai-object-detection",
    thumbnail: "https://drive.google.com/thumbnail?id=1dJzd12qRoMrCurdHTBDmJd98I-AQ2qNC&sz=w1000-h600"
  },
  {
      title: 'Anuvadini AI Agents',
      tag: 'Text, voice, and speech automation',
      desc:
        'Develop intelligent multilingual AI agents trained on websites, PDFs, and domain knowledge. Deploy role-access based dashboards with analytics and real-time model controls for every industry.',
      logo: logoAgents,
      gradient: 'from-[#008080] via-[#008080] to-[#008080]',
      buttonBg: 'bg-[#6d28d9]',
      shadow: 'shadow-purple-200',
      videoId: "anuvadini-ai-speakpic",
      thumbnail: "https://drive.google.com/thumbnail?id=1iEyeizPFOd4wtKF7t6KCp2VbP31twjoc&sz=w1000-h600"
    },
    {
      title: 'Anuvadini Bharatt',
      tag: 'Secure multilingual communication',
      desc:
        'Make-in-India and Aatmanirbhar Bharat solutions for secure, intelligent and multilingual communication.',
      logo: logoConnect,
      list: [
        {
          label: 'Hyped Samvadini Super App - Secure, seamless multilingual messaging',
          href: 'https://hyped.aicte-india.org/',
        },
        {
          label: 'Desi AI GenZ Keyboard - Type, speak, and translate in 22 Indian and 57+ global languages',
          href: 'https://desi.aicte-india.org/',
        },
        {
          label: 'Anuvadini Bharatt - AI-powered video conferencing for teams and webinars',
          href: 'https://anuvadinimeet.aicte-india.org/',
        },
      ],
      gradient: 'from-[#87CEEB] via-[#87CEEB] to-[#87CEEB]',
      buttonBg: 'bg-[#0284c7]',
      shadow: 'shadow-sky-200',
      videoId: "anuvadini-ai-voice-enabled-form-normal",
      thumbnail: "https://drive.google.com/thumbnail?id=1OCGRnJnestXM2A8eC871mWiRVSob2McE&sz=w1000-h600"
    },
    {
      title: 'Anuvadini Web & Mobile',
      tag: 'AI, BI, DI at massive scale',
      desc:
        '75+ customized solutions across supply chain, banking, education, legal systems, and real-time workflow tracking. Built for zettabytes of data, multilingual engagement, and sustainable growth.',
      logo: logoDevelop,
      gradient: 'from-[#BF868F] via-[#BF868F] to-[#BF868F]',
      buttonBg: 'bg-[#0f766e]',
      shadow: 'shadow-emerald-200',
      videoId: "anuvadini-ai-buddha-ji-presentation",
      thumbnail: "https://drive.google.com/thumbnail?id=1VVpDBK9IkJLa4HD3fSeiGLj7h4poiOvy&sz=w1000-h600",
      biometricApp: {
        title: "Anuvadini Biometric App",
        description: "The Anuvadini Biometric App is a secure, AI-powered attendance and identity verification solution that enables organizations to record attendance seamlessly using advanced biometric authentication methods. Designed for reliability and transparency, the app supports multiple biometric modalities including Face Recognition, Voice Authentication, Iris Scanning, and Palm Recognition, ensuring accurate identity validation.",
        geoFencing: "To maintain authenticity and prevent misuse, attendance can only be marked within the predefined geo-fenced location range of the organization. This ensures that employees, students, or staff can record attendance only when physically present at authorized premises.",
        dashboard: "The app also provides a comprehensive real-time dashboard that allows users and administrators to monitor attendance history, track trends, generate reports, and analyze workforce or institutional presence with actionable insights.",
        capabilities: [
          "Multi-Modal Biometric Authentication – Face, Voice, Iris, and Palm-based attendance marking.",
          "Geo-Fenced Attendance Control – Attendance allowed only within authorized location boundaries.",
          "Real-Time Attendance Dashboard – View attendance records, trends, and analytics instantly.",
          "Secure & Tamper-Resistant System – Prevents proxy attendance and unauthorized access.",
          "Role-Based Access Control – Separate views for administrators, managers, and users.",
          "Analytics & Reporting – Detailed insights for compliance, workforce management, and performance tracking."
        ],
        bestFor: "Government offices, educational institutions, corporate enterprises, factories, field workforce management, training centers, and organizations requiring secure, location-verified attendance systems."
      }
    },
];
const logoCards = [
  {
    title: "Anuvadini Translation",
    subtitle: "MULTILINGUAL LLM + HUMAN EXPERTISE",
    desc: "Create and translate content across 22 Indian and 89+ global languages using our in-house LLM and expert linguists. 4L+ publications created and 2B+ pages translated across 89+ domains with a two-level human validation process.",
    logo: "https://anuvadini.aicte-india.org/assets/Anuvadini_Translation-DKsoaMDC.png",
    gradient: "from-[#1e16f9] via-[#7b3cfb] to-[#f97316]",
    buttonBg: "bg-[#c2410c]",
    shadow: "shadow-orange-200",
    videoId: "anuvadini-ai-qa-generator",
    thumbnail: "https://anuvadini.aicte-india.org/assets/Anuvadini_Translation-DKsoaMDC.png",
    video:"https://youtu.be/pZvA6kkth-s?si=wf0E8tSHBMzR2pFL"
  },
  {
    title: "Desi AI GenZ Keyboard - World's First GenZ AI Keyboard",
    subtitle: "DRAG-AND-DROP PUBLISHING STUDIO",
    desc: "Design, format, and publish professional books, research papers, and novels without complex software. Collaborate in real time, apply advanced typography, and export InDesign, PDF, Word, PNG, and JPG formats instantly.",
    logo: "https://desi.aicte-india.org/img/Wryto-new-logo.svg",
    gradient: "from-[#4f46e5] via-[#6366f1] to-[#4f46e5]",
    buttonBg: "bg-[#3730a3]",
    shadow: "shadow-indigo-200",
    videoId: "anuvadini-print-demo-1",
    video: "https://youtu.be/DyZpz--HU64?si=MMXXEFMRLPA2C8JO"
  },
  {
    title: "Hyped Samvadini - Global Messaging Super App",
    subtitle: "MAKE-IN-INDIA GEOSPATIAL INTELLIGENCE",
    desc: "Unlock precise insights across coastlines, rivers, forests, and agriculture. Map urban assets, land use, and infrastructure, and monitor construction in real time for faster, data-driven decisions.",
    logo: "https://hyped.aicte-india.org/img/hyped.png",
    gradient: "from-[#1e16f9] via-[#7b3cfb] to-[#f97316]",
    buttonBg: "bg-[#c2410c]",
    shadow: "shadow-orange-200",
    videoId: "anuvadini-ai-qa-generator",
    thumbnail: "https://hyped.aicte-india.org/img/hyped.png",
    video:"https://youtu.be/a9U43E2lfFQ?si=rJYNsuvgDfMpLUtN"
  },
  {
      title: 'Anuvadini AI Agents',
      tag: 'Text, voice, and speech automation',
      desc:
        'Develop intelligent multilingual AI agents trained on websites, PDFs, and domain knowledge. Deploy role-access-based dashboards with analytics and real-time model controls for every industry.',
      logo: "https://anuvadinimeet.aicte-india.org/src/assets/connect-logo.png",
    gradient: "from-[#a7f3d0] via-[#bfdbfe] to-[#a7f3d0]",
    buttonBg: "bg-[#9d174d]",
    shadow: "shadow-rose-200",  
    videoId: "anuvadini-ai-object-detection",
    thumbnail: "https://drive.google.com/thumbnail?id=1dJzd12qRoMrCurdHTBDmJd98I-AQ2qNC&sz=w1000-h600",
    video: "https://youtu.be/8n9sXh2lHjA?si=7mNqj3Zt1aL8e5c0"
    },
    {
      title: "Anuvadini Print",
    subtitle: "DRAG-AND-DROP PUBLISHING STUDIO",
    desc: "Design, format, and publish professional books, research papers, and novels without complex software. Collaborate in real time, apply advanced typography, and export InDesign, PDF, Word, PNG, and JPG formats instantly.",
    logo: "https://anuvadini.aicte-india.org/assets/Anuvadini_Print-D-uK_-e7.png",
    gradient: "from-[#4f46e5] via-[#6366f1] to-[#4f46e5]",
    buttonBg: "bg-[#3730a3]",
    shadow: "shadow-indigo-200",
    videoId: "anuvadini-print-demo-1",
      thumbnail: "https://anuvadini.aicte-india.org/assets/Anuvadini_Print-D-uK_-e7.png",
      video: "https://drive.google.com/file/d/1A_4J_htxQ6FU6AwxFmyK0x0mY1YjZB_c/view"
    },
    {
      title: 'Anuvadini Web & Mobile',
      tag: 'AI, BI, DI at massive scale',
      desc:
        '75+ customized solutions across supply chain, banking, education, legal systems, and real-time workflow tracking. Built for zettabytes of data, multilingual engagement, and sustainable growth.',
      logo: logoDevelop,
      gradient: 'from-[#BF868F] via-[#BF868F] to-[#BF868F]',
      buttonBg: 'bg-[#0f766e]',
      shadow: 'shadow-emerald-200',
      videoId: "anuvadini-ai-buddha-ji-presentation",
      thumbnail: "https://drive.google.com/thumbnail?id=1VVpDBK9IkJLa4HD3fSeiGLj7h4poiOvy&sz=w1000-h600",
      video: "https://drive.google.com/file/d/1VVpDBK9IkJLa4HD3fSeiGLj7h4poiOvy/view",
      biometricApp: {
        title: "Anuvadini Biometric App",
        description: "The Anuvadini Biometric App is a secure, AI-powered attendance and identity verification solution that enables organizations to record attendance seamlessly using advanced biometric authentication methods. Designed for reliability and transparency, the app supports multiple biometric modalities including Face Recognition, Voice Authentication, Iris Scanning, and Palm Recognition, ensuring accurate identity validation.",
        features: [
          "Multi-Modal Biometric Authentication – Face, Voice, Iris, and Palm-based attendance marking.",
          "Geo-Fenced Attendance Control – Attendance allowed only within authorized location boundaries.",
          "Real-Time Attendance Dashboard – View attendance records, trends, and analytics instantly.",
          "Secure & Tamper-Resistant System – Prevents proxy attendance and unauthorized access.",
          "Role-Based Access Control – Separate views for administrators, managers, and users.",
          "Analytics & Reporting – Detailed insights for compliance, workforce management, and performance tracking."
        ],
        bestFor: "Government offices, educational institutions, corporate enterprises, factories, field workforce management, training centers, and organizations requiring secure, location-verified attendance systems."
      }
    },
];

type HeroProps = {
  onContact: () => void;
  onShowProductDetail?: (product: any, sectionId?: string) => void;
};

export const Hero = ({ onContact, onShowProductDetail }: HeroProps) => {
  const [selectedVideo, setSelectedVideo] = useState<{ url: string; title: string } | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const handleVideoPlay = (videoId: string, title: string) => {
    const videoUrl = generateDriveUrl(videoMappings[videoId as keyof typeof videoMappings]);
    setSelectedVideo({ url: videoUrl, title });
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };
  
  return (
    <section 
      id="hero" 
      className="relative pt-24 pb-24 px-4 min-h-screen scroll-mt-28 overflow-hidden"
      aria-label="Anuvadini AI Hero Section"
    >
      {/* Full Background Container - No White Background */}
      <div className="absolute inset-0 z-0">
        {/* CRAZY Animated Blue Background for Top Section */}
        <div className="absolute top-24 left-0 right-0 h-[103.5vh] sm:h-[90vh] md:h-[350px] lg:h-[16.5%] rounded-t-[3.5rem] overflow-hidden">
          {/* Base Darker Crazy Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#312e81] opacity-95" />
          
          {/* Animated Diagonal Stripes */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-pulse" />
          
          {/* Moving Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-[#cc0066]/20 via-transparent to-[#0066cc]/20 animate-pulse" 
               style={{ animationDelay: '1s', animationDuration: '3s' }} />
          
          {/* Geometric Pattern Overlay */}
          <div className="absolute inset-0 opacity-20" 
               style={{
                 backgroundImage: `
                   radial-gradient(circle at 25% 25%, #ffffff40 2px, transparent 2px),
                   radial-gradient(circle at 75% 75%, #ffffff60 1px, transparent 1px),
                   linear-gradient(45deg, transparent 40%, #ffffff20 50%, transparent 60%)
                 `,
                 backgroundSize: '60px 60px, 40px 40px, 80px 80px'
               }} />
          
          {/* Floating Orbs */}
          <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-cyan-300/30 to-blue-500/30 rounded-full blur-xl animate-bounce" 
               style={{ animationDuration: '4s' }} />
          <div className="absolute top-20 right-20 w-24 h-24 bg-gradient-to-br from-purple-300/40 to-pink-500/40 rounded-full blur-lg animate-bounce" 
               style={{ animationDuration: '3s', animationDelay: '1s' }} />
          <div className="absolute bottom-20 left-1/3 w-20 h-20 bg-gradient-to-br from-green-300/30 to-blue-400/30 rounded-full blur-md animate-bounce" 
               style={{ animationDuration: '5s', animationDelay: '2s' }} />
          
          {/* Mesh Gradient Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#2d5aa0]/30 via-[#1e3a8a]/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-tl from-[#1e40af]/20 via-transparent to-[#3730a3]/30" />
          
          {/* Dynamic Light Rays */}
          <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-white/40 via-white/10 to-transparent transform rotate-12 animate-pulse" />
          <div className="absolute top-0 right-1/3 w-1 h-full bg-gradient-to-b from-white/30 via-white/5 to-transparent transform -rotate-12 animate-pulse" 
               style={{ animationDelay: '1.5s' }} />
        </div>                        
        
        {/* Orange/Pink Gradient Container for Service Cards Section - Seamless Connection */}
        <div className="absolute top-[calc(118vh-2px)] sm:top-[calc(84vh-2px)] md:top-[calc(295px-2px)] lg:top-[calc(19%-2px)] left-0 right-0 bottom-24 rounded-b-[3.5rem] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f97316] via-[#db2777] to-[#4f46e5] opacity-90" />
          <div className="absolute inset-0 bg-black/5" />
        </div>
      </div>

      <div className="container mx-auto relative z-10 pt-16 mb-20">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Split Layout: Text Left (70%), Images Right (30%) */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-16 items-center mb-16">
            
            {/* Left Side - Text Content (70%) */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="col-span-1 lg:col-span-7 space-y-6 lg:space-y-8 order-1 lg:order-1"
            >
              <h1 className="text-base md:text-2xl lg:text-3xl xl:text-2xl   text-white">
                <span className="sr-only">Anuvadini AI: </span>
                Anuvadini AI is a human-in-the-loop Language intelligence, Artificial intelligence , Business intelligence , Data intelligence company delivering secure, scalable, multilingual, multimodal software , web , mobile,  geo platforms.
                <br />
                <span className="block mt-2">Supporting billions of users and interactions daily.</span>
              </h1>
            </motion.div>

            {/* Right Side - Small Animated Logos (30%) */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="col-span-1 lg:col-span-3 relative flex justify-center items-center order-2 lg:order-2"
            >
              <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 max-w-[220px] sm:max-w-[260px] lg:max-w-sm">
                {[
                  logoCards[0], // Anuvadini Translation
                  logoCards[1], // Anuvadini Print
                  logoCards[2], // Anuvadini GEO-AI
                  logoCards[4], // Anuvadini Bharatt (Connect)
                ].map((card, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ 
                      duration: 0.8, 
                      delay: 0.4 + index * 0.15,
                      type: "spring",
                      stiffness: 120,
                      damping: 12
                    }}
                    whileHover={{ 
                      scale: 1.15, 
                      rotate: index % 2 === 0 ? 5 : -5,
                      zIndex: 10,
                      transition: { duration: 0.2 }
                    }}
                    className="relative group cursor-pointer"
                    onMouseEnter={() => setHoveredCard(index)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => {
                      if (card.video) {
                        setSelectedVideo({ url: card.video, title: card.title });
                      }
                    }}
                  >
                    {/* Small Logo Container */}
                    <div className={`relative overflow-hidden rounded-lg sm:rounded-xl bg-gradient-to-br ${card.gradient} p-0.5 shadow-lg hover:shadow-xl transition-shadow duration-300`}>
                      <div className="relative bg-white rounded-md sm:rounded-lg overflow-hidden w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24">
                        <img 
                          src={card.logo}
                          alt={`${card.title} - AI translation and multilingual communication platform logo`}
                          className="w-full h-full object-contain p-1.5 sm:p-2 transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        
                        {/* Play Button Overlay - Smaller */}
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: hoveredCard === index ? 1 : 0 }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                            className="bg-white/90 backdrop-blur-sm rounded-full p-1.5 sm:p-2 shadow-lg"
                          >
                            <Play className="w-3 h-3 sm:w-4 sm:h-4 text-gray-800 ml-0.5" fill="currentColor" />
                          </motion.div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Continuous Floating Animation */}
                    <motion.div
                      animate={{ 
                        y: [0, -6, 0],
                        rotate: [0, 2, 0, -2, 0],
                        scale: [1, 1.02, 1]
                      }}
                      transition={{ 
                        duration: 3 + index * 0.5,
                        repeat: Infinity,
                        delay: index * 0.3,
                        ease: "easeInOut"
                      }}
                      className="absolute inset-0 pointer-events-none"
                    />
                    
                    {/* Glow Effect */}
                    <motion.div
                      animate={{ 
                        opacity: [0.3, 0.6, 0.3],
                        scale: [1, 1.1, 1]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.4
                      }}
                      className={`absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-br ${card.gradient} opacity-20 blur-sm -z-10`}
                    />
                  </motion.div>
                ))}
              </div>
              
              {/* Enhanced Decorative Elements - Hidden on mobile */}
              <motion.div
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  rotate: { duration: 25, repeat: Infinity, ease: "linear" },
                  scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                }}
                className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 w-4 h-4 lg:w-6 lg:h-6 bg-white/30 rounded-full blur-sm hidden sm:block"
              />
              <motion.div
                animate={{ 
                  rotate: -360,
                  y: [0, -8, 0]
                }}
                transition={{ 
                  rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                  y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                }}
                className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 w-3 h-3 lg:w-4 lg:h-4 bg-white/40 rounded-full blur-sm hidden sm:block"
              />
              <motion.div
                animate={{ 
                  x: [0, 8, 0],
                  opacity: [0.2, 0.5, 0.2]
                }}
                transition={{ 
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
                className="absolute top-1/2 -right-6 lg:-right-8 w-2 h-2 lg:w-3 lg:h-3 bg-white/25 rounded-full blur-sm hidden sm:block"
              />
            </motion.div>
          </div>
           <motion.div  
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="flex flex-col gap-4 sm:gap-3 rounded-[2.25rem] bg-white/20 px-4 sm:px-6 py-5 shadow-2xl shadow-black/20 ring-1 ring-white/40 backdrop-blur-md mb-16"
          >
            <div className="flex flex-col sm:flex-row justify-center sm:justify-between gap-3 sm:gap-4">
              {/* First stat - smaller width */}
              <span className="relative flex items-center gap-3 sm:gap-4 rounded-full bg-white/95 px-4 sm:px-5 py-3 text-xs md:text-sm font-black uppercase tracking-wide text-[#111827] shadow-lg shadow-black/10 ring-1 ring-white/70 w-full sm:w-auto sm:min-w-0 sm:flex-shrink-0">
                <span className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-white/70 via-white/30 to-white/70" />
                <span className="text-2xl sm:text-3xl md:text-4xl leading-none w-12 sm:w-20 text-center flex-shrink-0">206</span>
                <span className="text-lg md:text-xl text-[#4b5563]">|</span>
                 <span className="text-left text-xs md:text-sm font-semibold text-[#374151] tracking-normal normal-case leading-tight text-justify-inter-word flex-1 min-w-0">
                  <span className="font-bold sm:block">In-house technical</span>{" "}
                  <span className="font-bold sm:block">+ language</span>{" "}
                  <span className="font-bold sm:block">experts</span>
                </span>
              </span>
              
              {/* Middle stat - takes remaining full width */}
              <span className="relative flex items-center gap-3 sm:gap-4 rounded-full bg-white/95 px-4 sm:px-5 py-3 text-xs md:text-sm font-black uppercase tracking-wide text-[#111827] shadow-lg shadow-black/10 ring-1 ring-white/70 w-full sm:flex-1 sm:min-w-0">
                <span className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-white/70 via-white/30 to-white/70" />
                <span className="text-2xl sm:text-3xl md:text-4xl leading-none w-20 sm:w-24 text-center ">3500+</span>
                <span className="ml-0 md:ml-8 text-lg md:text-xl text-[#4b5563]">|</span>

                <span className="text-left text-xs md:text-sm font-semibold text-[#374151] tracking-normal normal-case leading-tight text-justify-inter-word ">
                  
                  <span className="block font-bold">Language and subject experts all over India</span>
                  
                </span>
              </span>
              
              {/* Third stat - smaller width */}
              <span className="relative flex items-center gap-3 sm:gap-4 rounded-full bg-white/95 px-4 sm:px-5 py-3 text-xs md:text-sm font-black uppercase tracking-wide text-[#111827] shadow-lg shadow-black/10 ring-1 ring-white/70 w-full sm:w-auto sm:min-w-0 sm:flex-shrink-0">
                <span className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-white/70 via-white/30 to-white/70" />
                <span className="text-2xl sm:text-3xl md:text-4xl leading-none w-12 sm:w-20 text-center flex-shrink-0">136+</span>
                <span className="text-lg md:text-xl text-[#4b5563]">|</span>
                <span className="text-left text-xs md:text-sm font-semibold text-[#374151] tracking-normal normal-case leading-tight text-justify-inter-word flex-1 min-w-0">
                  <span className="font-bold sm:block">National and</span>{" "}
                  <span className="font-bold sm:block">international</span>{" "}
                  <span className="font-bold sm:block">clients</span>
                </span>
              </span>
            </div>
          </motion.div>

          {/* Service Cards Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {serviceCards.map((card, index) => {
               const gradientClass =
    index === 0
      ? 'from-[#4f46e5] via-[#6366f1] to-[#4f46e5]'
      : card.gradient;

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
      className={`bg-gradient-to-b ${gradientClass} rounded-[2.5rem] p-4 lg:p-6 flex flex-col h-full ${card.shadow} border border-white/10 group hover:-translate-y-2 transition-transform duration-500`}
    >
                <div className="flex flex-col items-center gap-4 mb-6 lg:mb-8">
                  <div className="bg-white rounded-2xl p-3 lg:p-4 shadow-inner">
                    <img 
                      src={card.logo} 
                      alt={`${card.title} - Professional AI platform for ${card.subtitle || card.tag}`} 
                      className="h-12 lg:h-16 object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-center">
                    <h3 className={`${index === 4 ? 'text-[#1e3a8a] font-black' : index < 3 ? 'text-white font-black' : 'gradient-text-product'} text-2xl lg:text-3xl font-black mb-2 leading-tight`}>
                      {card.title}
                    </h3>
                    <p className={`${index === 4 ? 'text-black font-bold' : 'text-white/80'} text-[9px] lg:text-[10px] font-black tracking-[0.2em] uppercase text-justify-inter-word`}>
                      {card.subtitle || card.tag}
                    </p>
                  </div>
                </div>

                

                <p className={`${index === 4 ? 'text-black font-medium' : 'text-white/95'} text-base lg:text-lg leading-relaxed mb-4 lg:mb-6 font-medium text-justify-inter-word`}>
                  {card.desc}{" "}{onShowProductDetail && (
                    <button
                      type="button"
                      onClick={() => onShowProductDetail(card)}
                      className="text-white font-black text-[10px] lg:text-xs uppercase tracking-widest underline decoration-2 underline-offset-4 hover:text-yellow-200 hover:decoration-yellow-200 transition-colors cursor-pointer bg-transparent border-0 p-0 text-left"
                    >
                      VIEW MORE
                    </button>
                  )}
                </p>
                

                {card.list && (
                  <ul className="text-black text-sm font-bold leading-relaxed space-y-3 pl-5 mb-8 lg:mb-10 list-disc">
                    {card.list.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-justify-inter-word">
                        {typeof item === "string" ? item : (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noreferrer"
                            className="underline decoration-black/40 underline-offset-4 hover:decoration-black text-justify-inter-word"
                          >
                            {item.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                  
                )}
                {/* Product Lines Section - Show All Products */}
                {productNavigationLinks[card.title as keyof typeof productNavigationLinks] && (
                  <div className="mb-4 lg:mb-6">
                    <h4 className={`${index === 4 ? 'text-[#1e3a8a] font-bold' : 'text-white/90'} text-sm font-bold mb-3 uppercase tracking-wide text-center`}>Our Product Line:</h4>
                    <div className="flex flex-wrap justify-center gap-2">
                      {productNavigationLinks[card.title as keyof typeof productNavigationLinks]?.map((product, productIndex) => (
                        <button
                          key={productIndex}
                          onClick={() => onShowProductDetail?.(card, product.section)}
                          className={`group flex items-center gap-1.5 ${index === 4 ? 'bg-[#1e3a8a] hover:bg-[#1e40af] border-[#1e3a8a] hover:border-[#1e40af] text-white' : 'bg-white/10 hover:bg-white/20 border-white/10 hover:border-white/30'} rounded-lg px-2.5 py-1.5 transition-all duration-300 border`}
                        >
                          <span className={`${index === 4 ? 'text-white group-hover:text-white' : 'text-white/90 group-hover:text-white'} text-xs font-medium transition-colors whitespace-nowrap`}>
                            {product.title}
                          </span>
                          <ExternalLink className={`w-2.5 h-2.5 ${index === 4 ? 'text-white/80 group-hover:text-white' : 'text-white/60 group-hover:text-white/90'} transition-colors flex-shrink-0`} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons - 50/50 Row Layout */}
                {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 "> */}
                  <button
                    type="button"
                    onClick={onContact}
                    className=" mt-auto py-3 lg:py-4 bg-green-400 hover:bg-green-500 text-white rounded-2xl font-black text-[10px] lg:text-xs uppercase tracking-widest shadow-xl hover:scale-[1.02] transition-all active:scale-95 border border-white/5"
                  >
                    CONTACT ANUVADINI
                  </button>

                  
                {/* </div> */}
              </motion.div>)
})}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal
          isOpen={!!selectedVideo}
          onClose={closeVideoModal}
          videoUrl={selectedVideo.url}
          title={selectedVideo.title}
        />
      )}
    </section>
  );
};
